import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Loader2, Search } from "lucide-react";
import MiniHeader from "@/components/site/MiniHeader";
import AdminNav from "@/components/site/AdminNav";
import { ownerApi } from "@/lib/ownerApi";

const PAY = { unpaid: ["Not paid", "text-[#A0988E]"], reported: ["Says sent — check Cash App", "text-[#E6B89C]"], confirmed: ["Paid", "text-[#D4AF37]"] };
const FILM = { none: "Not started", uploaded: "Uploaded, not sent", sent: "Sent to family" };
const FILTERS = [
  ["all", "All", () => true],
  ["photos", "Waiting for photos", (r) => r.photos === 0],
  ["pay", "Waiting for payment", (r) => r.photos > 0 && r.payment_status === "unpaid"],
  ["check", "Check Cash App", (r) => r.payment_status === "reported"],
  ["make", "Ready to make", (r) => r.payment_status === "confirmed" && r.film_status !== "sent"],
  ["done", "Film sent", (r) => r.film_status === "sent"],
];

function Row({ r }) {
  const [payText, payColor] = PAY[r.payment_status];
  return (
    <a data-testid={`admin-request-${r.id}`} href={r.owner_path}
       className="group grid grid-cols-2 md:grid-cols-[1.6fr_1fr_0.6fr_1.1fr_1fr_auto] gap-x-4 gap-y-2 items-center px-5 py-4 border-b border-[rgba(197,165,90,0.12)] hover:bg-[rgba(212,175,55,0.05)] transition-colors">
      <div className="col-span-2 md:col-span-1 min-w-0">
        <div className="serif text-lg text-[#F5F0E8] truncate">{r.loved_one_name}</div>
        <div className="text-xs text-[#A0988E] truncate">{r.name} · {r.email}{r.phone ? ` · ${r.phone}` : ""}</div>
      </div>
      <div className="text-xs text-[#A0988E]">
        Requested {new Date(r.created_at).toLocaleDateString()}
        {r.service_date && <div className="text-[#E6B89C]">Service {r.service_date}</div>}
      </div>
      <div className="text-sm text-[#F5F0E8]">{r.photos} <span className="text-xs text-[#A0988E]">photos</span></div>
      <div className={`text-sm ${payColor}`}>{payText}</div>
      <div className="text-sm text-[#F5F0E8]/85">{FILM[r.film_status]}</div>
      <ArrowUpRight size={16} className="hidden md:block text-[#A0988E] group-hover:text-[#D4AF37]" />
    </a>
  );
}

export default function AdminRequestsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    ownerApi.get("/admin/requests").then(({ data }) => setRows(data.requests)).catch(() => navigate("/owner", { replace: true }));
  }, [navigate]);

  const shown = useMemo(() => {
    if (!rows) return [];
    const test = FILTERS.find(([k]) => k === filter)[2];
    const q = query.trim().toLowerCase();
    return rows.filter(test).filter((r) => !q || [r.loved_one_name, r.name, r.email, r.phone].some((v) => v?.toLowerCase().includes(q)));
  }, [rows, query, filter]);

  if (!rows) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#C5A55A]" /></div>;

  return (
    <div data-testid="admin-requests-page" className="min-h-screen">
      <MiniHeader right={<AdminNav />} />
      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
        <div className="overline mb-3">Owner</div>
        <h1 className="serif text-4xl sm:text-5xl font-light text-[#F5F0E8]">Families</h1>
        <div className="mt-8 flex flex-wrap gap-2">
          {FILTERS.map(([k, label, test]) => (
            <button key={k} data-testid={`admin-filter-${k}`} onClick={() => setFilter(k)}
                    className={`rounded-full px-4 py-2 text-xs border transition-colors ${filter === k ? "bg-[#D4AF37] text-[#0E0D0B] border-[#D4AF37]" : "border-[rgba(197,165,90,0.3)] text-[#F5F0E8]/85 hover:border-[#D4AF37]"}`}>
              {label} <span className="opacity-70">{rows.filter(test).length}</span>
            </button>
          ))}
        </div>
        <label className="mt-5 flex items-center gap-2 rounded-full border border-[rgba(197,165,90,0.25)] px-4 py-2.5 max-w-md focus-within:border-[#D4AF37]">
          <Search size={15} className="text-[#A0988E]" />
          <input data-testid="admin-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email or phone"
                 className="!bg-transparent !border-0 !p-0 !shadow-none !rounded-none flex-1 min-w-0 text-sm text-[#F5F0E8] placeholder:text-[#A0988E]/60 focus:outline-none" />
        </label>
        <div className="mt-6 card-surface overflow-hidden">
          <div className="hidden md:grid grid-cols-[1.6fr_1fr_0.6fr_1.1fr_1fr_auto] gap-x-4 px-5 py-3 text-[10px] tracking-widest uppercase text-[#C5A55A]/80 border-b border-[rgba(197,165,90,0.15)]">
            <span>Loved one · family</span><span>Dates</span><span>Photos</span><span>Payment</span><span>Film</span><span className="w-4" />
          </div>
          {shown.length ? shown.map((r) => <Row key={r.id} r={r} />) : (
            <p data-testid="admin-no-requests" className="px-5 py-10 text-center text-[#A0988E] spectral italic">No families match.</p>
          )}
        </div>
      </main>
    </div>
  );
}
